// The portal, assembled: the API client, the server-state cache, the router, localisation and the
// theme (spec/15 §2). `main.tsx` mounts it in the browser; the tests mount it with a fake API and a
// memory history.

import type { ApiClient } from '@clavian/api-client';
import { ClavianI18nProvider } from '@clavian/i18n/react';
import { ClavianThemeProvider, ToastProvider, TooltipProvider } from '@clavian/ui-web';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, type RouterHistory } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiProvider } from './api/client';
import { createPlayerI18n, DEFAULT_VENUE_LOCALE, type PlayerI18n } from './i18n';
import { createPlayerRouter } from './router';
import { ThemeChoiceProvider } from './shell/theme';

export function createPlayerQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // A refusal is an answer: retrying a 4xx only delays the screen that explains it.
        retry: (failures, error) =>
          failures < 2 && !(error instanceof Error && 'status' in error && Number((error as { status?: unknown }).status) < 500),
      },
    },
  });
}

export interface PlayerAppProps {
  readonly api: ApiClient;
  /** A memory history in tests; the browser's own otherwise. */
  readonly history?: RouterHistory;
  readonly i18n?: PlayerI18n;
}

/**
 * The portal is phone-heavy, so on a touch device it takes the kit's `touch` density — which lifts
 * every control to a 44 px target (BRAND.md §6.1 "Density"). A mouse keeps the comfortable density.
 * Guarded on `matchMedia` (absent in jsdom), so tests render at the comfortable default.
 */
function usePointerDensity(): 'comfortable' | 'touch' {
  const [coarse, setCoarse] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(pointer: coarse)');
    const onChange = (): void => setCoarse(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return coarse ? 'touch' : 'comfortable';
}

export function PlayerApp({ api, history, i18n: givenI18n }: PlayerAppProps): ReactNode {
  const [queryClient] = useState(createPlayerQueryClient);
  const [router] = useState(() => createPlayerRouter({ api, queryClient }, history));
  const [i18n] = useState(() => givenI18n ?? createPlayerI18n());
  const density = usePointerDensity();

  useEffect(() => {
    // The page says which language it is in, for screen readers and hyphenation.
    const sync = (language: string) => document.documentElement.setAttribute('lang', language);
    sync(i18n.language);
    i18n.on('languageChanged', sync);
    return () => i18n.off('languageChanged', sync);
  }, [i18n]);

  return (
    <ApiProvider api={api}>
      <QueryClientProvider client={queryClient}>
        <ClavianI18nProvider venue={DEFAULT_VENUE_LOCALE} i18n={i18n}>
          {/* Eclipse (ADR-0060), light or dark as the device or the player chose (ADR-0041); a venue's brand
              overrides it at runtime. Touch density on a phone, comfortable on a mouse (BRAND.md §6.1). */}
          <ThemeChoiceProvider>
            {(mode) => (
              <ClavianThemeProvider mode={mode} accent="brand" density={density} className="pw-root">
                <TooltipProvider>
                  <ToastProvider>
                    <RouterProvider router={router} />
                  </ToastProvider>
                </TooltipProvider>
              </ClavianThemeProvider>
            )}
          </ThemeChoiceProvider>
        </ClavianI18nProvider>
      </QueryClientProvider>
    </ApiProvider>
  );
}
